<template>
  <component
    :is="url ? 'a' : 'article'"
    class="ca-event-calendar-card flex flex-col justify-start gap-size-4 p-size-4"
    :class="{ 'ca-event-calendar-card--clickable': !!url }"
    v-bind="linkAttrs"
  >
    <span
      v-if="statusLabel"
      class="ca-event-calendar-card__status caption-small text-text-inverted gap-size-2 px-size-3 py-size-2 rounded-circle"
    >
      <RelogioIcon class="ca-event-calendar-card__icon" aria-hidden="true" />
      {{ statusLabel }}
    </span>

    <div class="ca-event-calendar-card__body flex gap-size-4">
      <time
        class="ca-event-calendar-card__date flex flex-col items-center justify-center"
        :datetime="startDate || undefined"
      >
        <span
          class="ca-event-calendar-card__month caption-medium text-neutrals-inverted-60"
        >
          {{ month }}
        </span>
        <span
          class="ca-event-calendar-card__day paragraph-bold text-text-inverted"
        >
          {{ day }}
        </span>
      </time>

      <div class="ca-event-calendar-card__content flex flex-col gap-size-2">
        <component
          :is="normalizeHeadingTag(titleHeadingType, 'p')"
          class="ca-event-calendar-card__title caption-action text-text-inverted"
        >
          {{ title }}
        </component>
        <component
          v-if="location"
          :is="normalizeHeadingTag(locationHeadingType, 'p')"
          class="ca-event-calendar-card__location caption-medium text-neutrals-inverted-60 flex items-center gap-size-2"
        >
          <LocalizacaoIcon
            class="ca-event-calendar-card__icon"
            aria-hidden="true"
          />
          {{ location }}
        </component>
      </div>
    </div>
  </component>
</template>

<script setup>
import { computed } from "vue";
import RelogioIcon from "@assets/Icon_Relogio.svg";
import LocalizacaoIcon from "@assets/Icon_Location.svg";
import { normalizeHeadingTag } from "../../../utils/heading";

defineOptions({
  name: "CaEventCalendarCard",
});

const props = defineProps({
  statusLabel: {
    type: String,
    default: "",
  },
  month: {
    type: String,
    default: "",
  },
  day: {
    type: String,
    default: "",
  },
  title: {
    type: String,
    default: "",
  },
  titleHeadingType: {
    type: String,
    default: "Paragraph",
  },
  location: {
    type: String,
    default: "",
  },
  locationHeadingType: {
    type: String,
    default: "Paragraph",
  },
  url: {
    type: String,
    default: "",
  },
  target: {
    type: String,
    default: "",
  },
  startDate: {
    type: String,
    default: "",
  },
});

const linkAttrs = computed(() => {
  if (!props.url) return {};

  const attrs = { href: props.url };
  if (props.target) {
    attrs.target = props.target;
    if (props.target === "_blank") attrs.rel = "noopener noreferrer";
  }
  return attrs;
});
</script>

<style scoped lang="scss">
.ca-event-calendar-card {
  border-radius: 20px;
  border: 1px solid var(--ca-color-neutrals-inverted-10);
  background: var(--ca-color-neutrals-inverted-10);
  text-decoration: none;
}

.ca-event-calendar-card--clickable {
  cursor: pointer;
  transition: background-color 0.2s ease-in-out;

  &:hover {
    background: var(--ca-color-neutrals-inverted-30);
  }

  &:active {
    background: var(--ca-color-neutrals-inverted-60);
  }

  &:focus-visible {
    outline: 2px solid var(--ca-color-outline-focus-only);
    outline-offset: 2px;
  }
}

.ca-event-calendar-card__status {
  display: inline-flex;
  align-self: flex-start;
  align-items: center;
  background: var(--ca-color-neutrals-inverted-10);
  white-space: normal;
  text-align: left;
}

.ca-event-calendar-card__icon {
  flex-shrink: 0;
  width: var(--size-4);
  height: var(--size-4);
}

.ca-event-calendar-card__body {
  align-items: flex-start;
}

.ca-event-calendar-card__date {
  flex-shrink: 0;
  text-align: center;
  white-space: nowrap;
}

.ca-event-calendar-card__content {
  flex: 1 0 0;
  min-width: 0;
}

.ca-event-calendar-card__title,
.ca-event-calendar-card__location {
  margin: 0;
  overflow-wrap: anywhere;
}
</style>
